import React, { useState, useEffect, useMemo } from 'react';
import { Shield } from 'lucide-react';
import { isPrerendering } from '../utils/analytics';

const CONSENT_KEY = 'datacalc_cookie_consent';

/**
 * Returns the stored consent value: 'granted', 'denied', or null (no choice yet).
 */
export const getStoredConsent = () => localStorage.getItem(CONSENT_KEY);

const GDPR_TIMEZONES = new Set([
  'Europe/Amsterdam','Europe/Andorra','Europe/Athens','Europe/Belfast',
  'Europe/Belgrade','Europe/Berlin','Europe/Bratislava','Europe/Brussels',
  'Europe/Bucharest','Europe/Budapest','Europe/Busingen','Europe/Chisinau',
  'Europe/Copenhagen','Europe/Dublin','Europe/Gibraltar','Europe/Guernsey',
  'Europe/Helsinki','Europe/Isle_of_Man','Europe/Istanbul','Europe/Jersey',
  'Europe/Kaliningrad','Europe/Kiev','Europe/Kirov','Europe/Kyiv',
  'Europe/Lisbon','Europe/Ljubljana','Europe/London','Europe/Luxembourg',
  'Europe/Madrid','Europe/Malta','Europe/Mariehamn','Europe/Minsk',
  'Europe/Monaco','Europe/Moscow','Europe/Nicosia','Europe/Oslo',
  'Europe/Paris','Europe/Podgorica','Europe/Prague','Europe/Riga',
  'Europe/Rome','Europe/Samara','Europe/San_Marino','Europe/Sarajevo',
  'Europe/Simferopol','Europe/Skopje','Europe/Sofia','Europe/Stockholm',
  'Europe/Tallinn','Europe/Tirane','Europe/Uzhgorod','Europe/Vaduz',
  'Europe/Vatican','Europe/Vienna','Europe/Vilnius','Europe/Warsaw',
  'Europe/Zagreb','Europe/Zaporozhye','Europe/Zurich',
  'Atlantic/Canary','Atlantic/Faroe','Atlantic/Madeira','Atlantic/Reykjavik',
]);

/**
 * Best-effort check for whether the visitor is likely in a GDPR region.
 * Uses the browser timezone — not perfect, but good enough to show the
 * right banner copy. The actual consent enforcement is handled server-side
 * by Google's tag infrastructure via the `region` parameter.
 */
const isLikelyGDPR = () => {
  try {
    return GDPR_TIMEZONES.has(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return false;
  }
};

/**
 * CookieConsent — Region-aware consent banner.
 *
 * GDPR regions (EEA/UK): opt-in model — cookies are off until the user
 * clicks "Accept". Copy explains this and asks for explicit consent.
 *
 * Everywhere else: opt-out model — cookies are active by default. Copy
 * informs the user and offers an "Opt out" button.
 */
const CookieConsent = ({ onConsent }) => {
  const [visible, setVisible] = useState(false);
  const gdpr = useMemo(isLikelyGDPR, []);

  useEffect(() => {
    // Never during react-snap builds — the banner would be baked into the
    // static HTML (the crawl often takes longer than this 1s timer)
    if (isPrerendering()) return;
    const stored = getStoredConsent();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleChoice = (choice) => {
    localStorage.setItem(CONSENT_KEY, choice);
    setVisible(false);
    if (onConsent) onConsent(choice);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-12 left-0 right-0 z-50 flex justify-center px-3 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-xl bg-surface-card rounded-xl shadow-lg border border-border p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 animate-slideUp">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Shield className="w-5 h-5 text-conform-red flex-shrink-0 mt-0.5" />
          <p className="text-sm text-ink-secondary leading-relaxed">
            {gdpr
              ? 'We\u2019d like to use analytics cookies to understand how our tools are used and improve your experience. No personal data is collected. May we enable analytics?'
              : 'We use anonymous analytics cookies to understand how our tools are used and improve your experience. No personal data is collected. You can opt out at any time.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <button
            onClick={() => handleChoice('denied')}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-ink-secondary bg-semantic-gray-100 hover:bg-semantic-gray-200 rounded-lg transition-colors"
          >
            {gdpr ? 'Decline' : 'Opt out'}
          </button>
          <button
            onClick={() => handleChoice('granted')}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-conform-red-deep hover:bg-conform-red-accent rounded-lg transition-colors"
          >
            {gdpr ? 'Accept' : 'OK'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
